import React, { useMemo } from 'react';
import { Stack, Typography } from '@mui/material';
import TutorialVideo from '@features/tutorial/shared/TutorialVideo';
import OnboardingSlidePager, {
  type OnboardingSlidePagerStep,
} from '../../shared/OnboardingSlidePager';

const EXPLANATION_STEPS = [
  {
    key: 'po-select',
    description:
      'Select the products you want to reorder. Juststock shows what needs restocking based on your forecast.',
    videoSrc: '/assets/videos/onboarding/po-select.mp4',
    ariaLabel: 'How to select products for a purchase order',
  },
  {
    key: 'po-draft',
    description:
      'Review quantities and lead times in your draft, then send the purchase order to your supplier.',
    videoSrc: '/assets/videos/onboarding/po-draft.mp4',
    ariaLabel: 'How to create a purchase order draft',
  },
] as const;

interface PurchaseOrderIntroStepProps {
  onComplete: () => void;
}

const PurchaseOrderIntroStep: React.FC<PurchaseOrderIntroStepProps> = ({
  onComplete,
}) => {
  const steps = useMemo<OnboardingSlidePagerStep[]>(
    () =>
      EXPLANATION_STEPS.map((step) => ({
        key: step.key,
        content: ({ active }) => (
          <Stack direction="column" gap={2}>
            <Typography variant="body1" color="text.secondary">
              {step.description}
            </Typography>
            <TutorialVideo
              src={step.videoSrc}
              ariaLabel={step.ariaLabel}
              active={active}
            />
          </Stack>
        ),
      })),
    [],
  );

  return (
    <OnboardingSlidePager
      title="How purchase orders work"
      nextLabel="Next"
      doneLabel="Get started"
      onComplete={onComplete}
      steps={steps}
    />
  );
};

export default PurchaseOrderIntroStep;
